import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiHome, FiMonitor, FiLayers, FiUsers,
  FiFileText, FiLogOut, FiMenu, FiX, FiShield, FiDownload
} from 'react-icons/fi';
import { useLanguage } from '../i18n/LanguageContext';

const Layout = ({ children }: { children: React.ReactNode }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();

  const menuItems = [
    { path: '/', icon: FiHome, label: t('dashboard') },
    { path: '/devices', icon: FiMonitor, label: t('devices') },
    { path: '/groups', icon: FiLayers, label: t('groups') },
    { path: '/users', icon: FiUsers, label: t('users') },
    { path: '/download', icon: FiDownload, label: t('download') },
    { path: '/audit', icon: FiFileText, label: t('logs') },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-slate-900">
      <div className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-slate-800 transition-all duration-300 flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <FiShield className="text-blue-500 text-2xl" />
              <span className="text-white font-bold text-lg">BrickManager</span>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400 hover:text-white p-1">
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <Icon className="text-xl flex-shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-700 space-y-2">
          {sidebarOpen && (
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => setLanguage('en')}
                className={`flex-1 py-1 rounded-lg text-sm font-semibold transition-colors ${
                  language === 'en'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('uk')}
                className={`flex-1 py-1 rounded-lg text-sm font-semibold transition-colors ${
                  language === 'uk'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                UA
              </button>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-700 hover:text-white w-full transition-colors"
          >
            <FiLogOut className="text-xl flex-shrink-0" />
            {sidebarOpen && <span>{t('logout')}</span>}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
